import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { getAnalyticsConfig } from "../lib/analytics.functions";
import { analyticsBootstrap, TAG_MANAGER_ID, trackPageView } from "../lib/analytics";
import { observeSitePage } from '../lib/site-observer';

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: () => getAnalyticsConfig(),
  head: ({ loaderData }) => ({
    scripts: [
      { id: "aleph-analytics", children: analyticsBootstrap(loaderData?.measurementId ?? null) },
      ...(loaderData?.measurementId ? [{ id: "aleph-ga", async: true, src: `https://www.googletagmanager.com/gtag/js?id=${loaderData.measurementId}` }] : []),
    ],
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Aleph Comex" },
      { name: "description", content: "Comércio exterior, procurement, exportação e logística integrada." },
      { name: "author", content: "Aleph Comex" },
      { property: "og:site_name", content: "Aleph Comex" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      { rel: "ai-catalog", href: "/.well-known/ai-catalog.json", type: "application/json" },
      { rel: "llms-txt", href: "/llms.txt", type: "text/markdown" },
      { rel: "preload", href: "/fonts/manrope-400-latin.woff2", as: "font", type: "font/woff2", crossOrigin: "anonymous" },
      { rel: "preload", href: "/fonts/montserrat-600-latin.woff2", as: "font", type: "font/woff2", crossOrigin: "anonymous" },

    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const lang = pathname.startsWith("/en") ? "en" : pathname.startsWith("/es") ? "es" : "pt-BR";
  return (
    <html lang={lang}>
      <head>
        <HeadContent />
      </head>
      <body>
        <noscript>
          <iframe title="Google Tag Manager" src={`https://www.googletagmanager.com/ns.html?id=${TAG_MANAGER_ID}`} height="0" width="0" hidden />
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const { measurementId } = Route.useLoaderData();
  const href = useRouterState({ select: (state) => state.location.href });
  useEffect(() => {
    trackPageView(measurementId);
  }, [href, measurementId]);
  useEffect(() => observeSitePage(), [href]);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
    </QueryClientProvider>
  );
}
